/** Header teleport target for marketplace pill search autocomplete. */
export const PILL_SEARCH_HEADER_ID = "header-searchbox-mobile";

export const PILL_SEARCH_PANEL_OPEN_CLASS = "header__searchbox-mobile--panel-open";

const PILL_SEARCH_PANEL_CLASS = "aa-Panel--pill-search";
const PILL_SEARCH_PANEL_SELECTOR = `.aa-Panel.${PILL_SEARCH_PANEL_CLASS}`;
const PILL_SEARCH_PANEL_GAP_PX = 6;
const PILL_SEARCH_PANEL_MAX_HEIGHT_PX = 400;

/** Blade + Vue home hero autocomplete container ids. */
const HOME_SEARCH_CONTAINER_IDS = new Set([
    "home-autocomplete",
    "search-autocomplete-home",
]);

export const HOME_SEARCH_BOX_OPEN_CLASS = "search-box--panel-open";

// Home hero panel uses Algolia's native positioning (no viewport anchoring),
// so only the open-state class is needed for the flush rounded-corner styling.
const HOME_SEARCH_PANEL_CLASS = "aa-Panel--home-search";

const VIEWPORT_ANCHORED_CLASS = "aa-Panel--viewport-anchored";

type ViewportAnchorSync = {
    anchor: HTMLElement;
    panelSelector: string;
    marginTop: number;
    maxLayoutHeight: number;
};

let viewportAnchorSync: ViewportAnchorSync | null = null;
let viewportAnchorCleanup: (() => void) | null = null;
let viewportAnchorPanel: HTMLElement | null = null;

export function isPillSearchAutocompleteContainer(
    containerId: string,
): boolean {
    const el = document.getElementById(containerId);
    return !!el?.closest(`#${PILL_SEARCH_HEADER_ID}`);
}

export function isHomeSearchAutocompleteContainer(
    containerId: string,
): boolean {
    if (HOME_SEARCH_CONTAINER_IDS.has(containerId)) {
        return true;
    }

    const el = document.getElementById(containerId);
    return !!el?.closest(".hero .search-box");
}

function resetViewportAnchoredPanel(panel: HTMLElement): void {
    panel.classList.remove(VIEWPORT_ANCHORED_CLASS);
    panel.style.position = "";
    panel.style.top = "";
    panel.style.left = "";
    panel.style.right = "";
    panel.style.width = "";
    panel.style.maxWidth = "";

    const layout = panel.querySelector<HTMLElement>(".aa-PanelLayout");
    if (layout) {
        layout.style.maxHeight = "";
    }
}

/**
 * Writes a style property only when its value changes. The panel's `style` attribute is
 * observed (see `startViewportAnchorSync`): writing identical values would notify the
 * observer again for nothing.
 */
function setStyleIfChanged(
    el: HTMLElement,
    property: "position" | "top" | "left" | "right" | "width" | "maxWidth" | "maxHeight",
    value: string,
): void {
    if (el.style[property] !== value) {
        el.style[property] = value;
    }
}

function positionViewportAnchoredPanel(
    anchor: HTMLElement,
    panel: HTMLElement,
    marginTop: number,
    maxLayoutHeight: number,
): void {
    const rect = anchor.getBoundingClientRect();
    const top = Math.round(rect.bottom + marginTop);
    const width = Math.round(Math.min(rect.width, window.innerWidth - 24));
    const left = Math.round(
        Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
    );
    const maxHeight = Math.max(
        0,
        Math.min(maxLayoutHeight, window.innerHeight - top - 16),
    );

    if (!panel.classList.contains(VIEWPORT_ANCHORED_CLASS)) {
        panel.classList.add(VIEWPORT_ANCHORED_CLASS);
    }
    setStyleIfChanged(panel, "position", "fixed");
    setStyleIfChanged(panel, "top", `${top}px`);
    setStyleIfChanged(panel, "left", `${left}px`);
    setStyleIfChanged(panel, "right", "auto");
    setStyleIfChanged(panel, "width", `${width}px`);
    setStyleIfChanged(panel, "maxWidth", `${width}px`);

    const layout = panel.querySelector<HTMLElement>(".aa-PanelLayout");
    if (layout) {
        setStyleIfChanged(layout, "maxHeight", `${maxHeight}px`);
    }
}

let panelStyleObserver: MutationObserver | null = null;

function syncViewportAnchoredPanel(): void {
    if (!viewportAnchorSync) {
        return;
    }

    const panel =
        document.querySelector<HTMLElement>(
            viewportAnchorSync.panelSelector,
        ) ?? viewportAnchorPanel;

    if (!panel || !document.body.contains(panel)) {
        return;
    }

    if (panel !== viewportAnchorPanel) {
        // Algolia rewrites the panel's inline position after each render, in a
        // requestAnimationFrame (`setPanelPosition`: absolute top, `width: unset`).
        // Watching the style attribute lets us take the position back in the same
        // frame, before paint, so only one position is ever painted.
        panelStyleObserver?.disconnect();
        panelStyleObserver?.observe(panel, {
            attributes: true,
            attributeFilter: ["style"],
        });
    }

    viewportAnchorPanel = panel;
    positionViewportAnchoredPanel(
        viewportAnchorSync.anchor,
        panel,
        viewportAnchorSync.marginTop,
        viewportAnchorSync.maxLayoutHeight,
    );
}

function stopViewportAnchorSync(): void {
    viewportAnchorCleanup?.();
    viewportAnchorCleanup = null;

    if (viewportAnchorPanel) {
        resetViewportAnchoredPanel(viewportAnchorPanel);
    }

    viewportAnchorSync = null;
    viewportAnchorPanel = null;
}

function startViewportAnchorSync(options: ViewportAnchorSync): void {
    // Algolia calls `onStateChange` on every state change while the panel is open
    // (each keystroke, each hovered suggestion). Restarting here used to wipe the
    // panel's position and restore it a frame later: the list jumped between two
    // positions and widths while the pointer moved over it. Keep the running sync.
    if (
        viewportAnchorSync &&
        viewportAnchorSync.anchor === options.anchor &&
        viewportAnchorSync.panelSelector === options.panelSelector
    ) {
        viewportAnchorSync = options;
        syncViewportAnchoredPanel();
        return;
    }

    stopViewportAnchorSync();
    viewportAnchorSync = options;

    const observer = new MutationObserver(() => {
        syncViewportAnchoredPanel();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const styleObserver = new MutationObserver(() => {
        syncViewportAnchoredPanel();
    });
    panelStyleObserver = styleObserver;

    const onViewportChange = () => {
        syncViewportAnchoredPanel();
        // Algolia repositions on resize via rAF — run again after it does.
        requestAnimationFrame(syncViewportAnchoredPanel);
    };

    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);

    viewportAnchorCleanup = () => {
        observer.disconnect();
        styleObserver.disconnect();
        if (panelStyleObserver === styleObserver) {
            panelStyleObserver = null;
        }
        window.removeEventListener("scroll", onViewportChange, true);
        window.removeEventListener("resize", onViewportChange);
    };

    syncViewportAnchoredPanel();
    requestAnimationFrame(syncViewportAnchoredPanel);
}

function handleSearchPanelOpenChange(
    isOpen: boolean,
    anchor: HTMLElement | null | undefined,
    panelSelector: string,
    marginTop: number,
    maxLayoutHeight: number,
): void {
    if (!isOpen || !anchor) {
        stopViewportAnchorSync();
        return;
    }

    startViewportAnchorSync({
        anchor,
        panelSelector,
        marginTop,
        maxLayoutHeight,
    });
}

export function clearPillSearchPanelOpenState(): void {
    stopViewportAnchorSync();
    document
        .getElementById(PILL_SEARCH_HEADER_ID)
        ?.classList.remove(PILL_SEARCH_PANEL_OPEN_CLASS);
}

// The home panel keeps Algolia's native positioning and never starts the viewport
// anchor sync: closing or unmounting it must not stop the header panel's sync.
export function clearHomeSearchPanelOpenState(): void {
    document
        .querySelector(".hero .search-box")
        ?.classList.remove(HOME_SEARCH_BOX_OPEN_CLASS);
}

export function pillSearchPanelLiantOptions(): {
    classNames: { panel: string };
    onStateChange: (params: { state: { isOpen: boolean } }) => void;
} {
    const searchbox = () => document.getElementById(PILL_SEARCH_HEADER_ID);

    return {
        classNames: {
            panel: `aa-Panel ${PILL_SEARCH_PANEL_CLASS}`,
        },
        onStateChange({ state }) {
            const searchboxEl = searchbox();
            searchboxEl?.classList.toggle(
                PILL_SEARCH_PANEL_OPEN_CLASS,
                state.isOpen,
            );
            handleSearchPanelOpenChange(
                state.isOpen,
                searchboxEl?.querySelector<HTMLElement>(".aa-Form"),
                PILL_SEARCH_PANEL_SELECTOR,
                PILL_SEARCH_PANEL_GAP_PX,
                PILL_SEARCH_PANEL_MAX_HEIGHT_PX,
            );
        },
    };
}

export function homeSearchPanelLiantOptions(containerId: string): {
    classNames: { panel: string };
    onStateChange: (params: { state: { isOpen: boolean } }) => void;
} {
    const searchBox = () =>
        document.getElementById(containerId)?.closest(".search-box") ?? null;

    return {
        classNames: {
            panel: `aa-Panel ${HOME_SEARCH_PANEL_CLASS}`,
        },
        onStateChange({ state }) {
            const searchBoxEl = searchBox();
            searchBoxEl?.classList.toggle(
                HOME_SEARCH_BOX_OPEN_CLASS,
                state.isOpen,
            );
        },
    };
}

export function resolveSearchPanelLiantOptions(
    containerId: string,
): ReturnType<typeof pillSearchPanelLiantOptions> | null {
    if (isPillSearchAutocompleteContainer(containerId)) {
        return pillSearchPanelLiantOptions();
    }

    if (isHomeSearchAutocompleteContainer(containerId)) {
        return homeSearchPanelLiantOptions(containerId);
    }

    return null;
}
